/**
 * A list filter that names another object's record (R5, `kind: "entity"`): the list page draws the command dialog's
 * name picker over that object's list, and the value stays what the list endpoint takes (the record's list key, or
 * the column the filter's `entity_value` names), so related lists and existing links keep working.
 *
 * A link carries only the value, so the picker resolves the name to show: first from the filtered rows themselves
 * (the one column that names the same object, a quote's customer name), else by searching the named object's list
 * for the value and reading the matching row's title. Neither found leaves the value shown as it is.
 *
 * A filter narrows history, so its picker widens the named list where that list declares how (`picker_value`: the
 * Users list's state set to all), and a record outside the list's default says so beside its name ("Lee Gone
 * (left)"). A command field's picker does neither: work is never given to someone who has left.
 */
import type {
  ChildRow,
  ManifestList,
  ManifestListFilter,
  ManifestObject,
} from "./contract";
import type { ManifestIndex } from "./manifest";
import { formatValue } from "./values";

export interface EntityFilterTarget {
  object: ManifestObject;
  list: ManifestList;
  /** The column of the named object's rows the filter's value is. */
  valueField: string;
  /** The named list's filters its own declaration widens for a picker (`picker_value`), as the list query takes them. */
  filters: Record<string, string>;
}

/**
 * What an entity filter picks from: null for any other filter, and for one naming an object the person cannot list
 * (not in the manifest yet, switched off, or list-less), which the page draws as a typed box instead.
 */
export function entityFilterTarget(
  index: ManifestIndex,
  filter: ManifestListFilter,
): EntityFilterTarget | null {
  if (filter.kind !== "entity" || !filter.object) return null;
  if (!index.isObjectEnabled(filter.object)) return null;
  const object = index.objectOf(filter.object);
  if (!object.list) return null;
  return {
    object,
    list: object.list,
    valueField: filter.entity_value ?? object.list.key_field,
    filters: Object.fromEntries(
      object.list.filters
        .filter((f) => f.picker_value != null)
        .map((f) => [f.param, f.picker_value!]),
    ),
  };
}

/**
 * The marker a picked record outside the named list's default carries beside its name: the row's value of a widened
 * filter, in brackets and lower case ("(left)" for a user who has left). Null for a record the list shows by default.
 */
export function outsideDefaultMarker(
  target: EntityFilterTarget,
  row: ChildRow,
): string | null {
  for (const filter of target.list.filters) {
    if (filter.picker_value == null || typeof filter.default !== "string")
      continue;
    const value = row[filter.param];
    if (typeof value !== "string" || value === filter.default) continue;
    const label = filter.options.find(([v]) => v === value)?.[1] ?? value;
    return `(${label.toLowerCase()})`;
  }
  return null;
}

/** A record's name with its marker when it is outside the named list's default ("Lee Gone (left)"). */
export function markedName(
  target: EntityFilterTarget,
  row: ChildRow,
  name: string,
): string {
  const marker = outsideDefaultMarker(target, row);
  return marker ? `${name} ${marker}` : name;
}

function shown(value: unknown): string | null {
  if (value == null || value === "") return null;
  return formatValue(value, "text");
}

/**
 * The chosen record's name from the rows the filter narrowed to: every row names it, in the one column of the list
 * that names the same object (`ref`). Null when no row is loaded, or no single column names the object.
 */
export function nameFromRows(
  list: ManifestList,
  target: EntityFilterTarget,
  rows: readonly ChildRow[],
): string | null {
  const naming = list.columns.filter((c) => c.ref === target.object.id);
  if (naming.length !== 1) return null;
  const field = naming[0]!.field;
  for (const row of rows) {
    const name = shown(row[field]);
    if (name) return name;
  }
  return null;
}

/** The chosen record's name from a search of its own list: the title of the row whose value field is the value. */
export function nameFromMatches(
  target: EntityFilterTarget,
  rows: readonly ChildRow[],
  value: string,
): string | null {
  const match = rows.find((row) => String(row[target.valueField]) === value);
  const name = match ? shown(match[target.list.title_field]) : null;
  return match && name ? markedName(target, match, name) : name;
}
